import { useMutation } from "@tanstack/react-query"
import {
	onAuthStateChanged,
	signInWithPopup,
	signOut,
	type User,
} from "firebase/auth"
import type React from "react"
import { createContext, useContext, useEffect, useState } from "react"
import { apiUrl } from "../lib/api-client"
import { auth, googleProvider } from "./firebase"
import { getStoredLocale } from "./i18n"

interface AuthContextType {
	user: User | null
	loading: boolean
	loginWithGoogle: () => Promise<void>
	logout: () => Promise<void>
	getIdToken: () => Promise<string | null>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

async function syncBackend(token: string): Promise<void> {
	// Plain fetch (not apiFetch) because we're inside the Firebase
	// auth-state callback — `auth.currentUser` may not yet reflect the just-
	// signed-in user, so we pass the fresh token through explicitly.
	const res = await fetch(apiUrl("/api/common/auth/sync"), {
		method: "POST",
		headers: {
			Authorization: `Bearer ${token}`,
			"Content-Type": "application/json",
			"Accept-Language": getStoredLocale(),
		},
	})
	if (!res.ok) {
		throw new Error(`Sync failed: ${res.status} ${res.statusText}`)
	}
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
	children,
}) => {
	const [user, setUser] = useState<User | null>(null)
	const [loading, setLoading] = useState(true)
	const { mutateAsync: syncWithBackend } = useMutation({
		mutationFn: syncBackend,
	})

	useEffect(() => {
		const unsubscribe = onAuthStateChanged(auth, async (user) => {
			if (user) {
				try {
					const token = await user.getIdToken()
					await syncWithBackend(token)
				} catch (error) {
					console.error("Auth sync failed, signing out:", error)
					await signOut(auth)
					setUser(null)
					setLoading(false)
					return
				}
			}
			setUser(user)
			setLoading(false)
		})
		return unsubscribe
	}, [syncWithBackend])

	const loginWithGoogle = async () => {
		await signInWithPopup(auth, googleProvider)
	}

	const logout = async () => {
		await signOut(auth)
	}

	const getIdToken = async () => {
		return user ? await user.getIdToken() : null
	}

	return (
		<AuthContext.Provider
			value={{ user, loading, loginWithGoogle, logout, getIdToken }}
		>
			{children}
		</AuthContext.Provider>
	)
}

export const useAuth = () => {
	const context = useContext(AuthContext)
	if (context === undefined) {
		throw new Error("useAuth must be used within an AuthProvider")
	}
	return context
}
